import { clsx } from "clsx";
import { Download, File, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslations } from "../../i18n";
import { isImageFile } from "../../utils/file";
import { formatFileSize } from "../../utils/formatFileSize";
import { IconButton } from "../Button/IconButton";
import { Message } from "../Message";
import { Text } from "../Text";
import { TruncatedContent } from "../TruncatedContent";
import type { IAttachmentProps } from "./types";

export const Attachment = (props: IAttachmentProps) => {
  const {
    file,
    onRemove,
    onDownload,
    invalid,
    message,
    showFileSize = true,
    name,
    size,
    downloading,
    deleting,
  } = props;
  const { t } = useTranslations();
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);

  const rootClass = clsx(
    "nuco-attachment",
    invalid && "nuco-attachment--invalid",
  );

  useEffect(() => {
    if (file && isImageFile(file)) {
      const previewUrl = URL.createObjectURL(file);
      setImagePreviewUrl(previewUrl);

      return () => {
        URL.revokeObjectURL(previewUrl);
      };
    }

    setImagePreviewUrl(null);
  }, [file]);

  const handleRemove = useCallback(() => {
    if (onRemove) {
      onRemove(file);
    }
  }, [file, onRemove]);

  const handleDownload = useCallback(() => {
    if (onDownload) {
      onDownload(file);
    }
  }, [file, onDownload]);

  return (
    <div className={rootClass}>
      <div className="nuco-attachment__content">
        <div className="nuco-attachment__icon">
          {imagePreviewUrl ? (
            // Decorative: the file name is right next to it.
            <img
              src={imagePreviewUrl}
              alt=""
              className="nuco-attachment__image-preview"
            />
          ) : (
            <File aria-hidden="true" />
          )}
        </div>
        <div className="nuco-attachment__details">
          <TruncatedContent
            className="nuco-attachment__name nuco-text nuco-text--md"
            content={name || file?.name || "-"}
          />
          {showFileSize && (
            <Text as="div" className="nuco-attachment__size">
              {size ? size : file ? formatFileSize(file.size) : "-"}
            </Text>
          )}
        </div>
        {(onRemove || onDownload) && (
          <div className="nuco-attachment__actions">
            {onDownload && (
              <IconButton
                size="md"
                color="neutral"
                variant="soft"
                content={<Download />}
                onClick={handleDownload}
                loading={downloading}
                aria-label={t("ui.fileUploader.download")}
              />
            )}
            {onRemove && (
              <IconButton
                size="md"
                color="neutral"
                variant="ghost"
                content={<Trash2 />}
                onClick={handleRemove}
                loading={deleting}
                aria-label={t("ui.fileUploader.remove")}
              />
            )}
          </div>
        )}
      </div>
      {message && <Message text={message} color="danger" />}
    </div>
  );
};
